// Sprint 6 — Reservas Recurrentes

import { css, cx } from "styled-system/css";
import {
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  PauseCircle,
  Pencil,
} from "lucide-react";
import { cf } from "@/components/brand/sfturnos-styles";
import type { RecurringRow } from "@/components/dashboard/create-recurring-modal";

export function RecurringBookingTable({
  rows,
  loading,
  page,
  totalPages,
  onPageChange,
  onEdit,
  onDeactivate,
}: {
  rows: RecurringRow[];
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  onEdit: (row: RecurringRow) => void;
  onDeactivate: (row: RecurringRow) => void;
}) {
  const pagBtn = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    p: "2",
    rounded: "md",
    borderWidth: "1px",
    borderColor: "#e2e8f0",
    bg: "white",
    cursor: "pointer",
    color: "#475569",
    _hover: { bg: "#f8fafc" },
    _disabled: { opacity: 0.45, cursor: "not-allowed" },
  });

  return (
    <div>
      <div className={cx(cf.dashSurface, css({ overflow: "hidden" }))}>
        <div className={css({ overflowX: "auto" })}>
          <table className={css({ w: "full", minW: "720px", borderCollapse: "collapse", fontSize: "sm" })}>
            <thead>
              <tr className={css({ bg: "#f8fafc", textAlign: "left" })}>
                <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Cliente</th>
                <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Frecuencia</th>
                <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Hora</th>
                <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Próxima fecha</th>
                <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold", textAlign: "right" })}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className={css({ py: "8", textAlign: "center", color: "#94a3b8" })}>
                    Cargando…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className={css({ py: "8", textAlign: "center", color: "#94a3b8" })}>
                    No hay series en esta pestaña.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className={css({ borderTopWidth: "1px", borderColor: "#e2e8f0" })}>
                    <td className={css({ py: "3", px: "4", color: "#0f172a" })}>
                      <div className={css({ fontWeight: "medium" })}>{r.clientName}</div>
                      <div className={css({ fontSize: "xs", color: "#64748b" })}>{r.clientEmail}</div>
                    </td>
                    <td className={css({ py: "3", px: "4", color: "#334155" })}>{r.frequencyLabel}</td>
                    <td className={css({ py: "3", px: "4", color: "#334155", whiteSpace: "nowrap" })}>{r.startTime}</td>
                    <td className={css({ py: "3", px: "4", color: "#334155", whiteSpace: "nowrap" })}>
                      {r.nextOccurrenceAt
                        ? new Date(r.nextOccurrenceAt).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })
                        : "—"}
                    </td>
                    <td className={css({ py: "3", px: "4", textAlign: "right" })}>
                      <div className={css({ display: "inline-flex", gap: "1", justifyContent: "flex-end" })}>
                        <button
                          type="button"
                          className={pagBtn}
                          aria-label="Editar"
                          onClick={() => onEdit(r)}
                        >
                          <Pencil size={18} strokeWidth={2} />
                        </button>
                        {r.isActive ? (
                          <button
                            type="button"
                            className={pagBtn}
                            aria-label="Desactivar"
                            onClick={() => onDeactivate(r)}
                          >
                            <PauseCircle size={18} strokeWidth={2} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 ? (
        <div className={css({ display: "flex", alignItems: "center", justifyContent: "center", gap: "2", mt: "4" })}>
          <button
            type="button"
            className={pagBtn}
            disabled={page <= 1}
            aria-label="Primera página"
            onClick={() => onPageChange(1)}
          >
            <ChevronFirst size={20} />
          </button>
          <button
            type="button"
            className={pagBtn}
            disabled={page <= 1}
            aria-label="Página anterior"
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft size={20} />
          </button>
          <span className={css({ fontSize: "sm", color: "#64748b", px: "3" })}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className={pagBtn}
            disabled={page >= totalPages}
            aria-label="Página siguiente"
            onClick={() => onPageChange(page + 1)}
          >
            <ChevronRight size={20} />
          </button>
          <button
            type="button"
            className={pagBtn}
            disabled={page >= totalPages}
            aria-label="Última página"
            onClick={() => onPageChange(totalPages)}
          >
            <ChevronLast size={20} />
          </button>
        </div>
      ) : null}
    </div>
  );
}
